/**
 * Which modal `<dialog>`s are open, innermost last — and the one place in each where something
 * that must stay usable ABOVE the dialog can live.
 *
 * PRIVATE to this package: `index.ts` does not re-export it.
 *
 * Opening a dialog with `showModal()` puts it in the browser's top layer and makes everything
 * outside it inert, which is exactly right for a form and exactly wrong for a toast: a failed
 * save raised from inside the dialog would paint BEHIND it, could not be clicked or focused, and
 * would sit under the `aria-hidden` the dialog puts on the page, so nobody would hear it either.
 *
 * So each open modal registers a "slot" (an empty element at the end of its box) and the toast
 * provider moves its viewport into the topmost one for as long as it is open. The slot belongs to
 * the dialog, so the viewport is not inert, not hidden, and is reached by the dialog's own Tab
 * order.
 */

/** Marks the element the toast provider keeps its viewport in, so a dialog never hides it. */
export const TOAST_HOST_ATTRIBUTE = 'data-toast-host';

const slots: HTMLElement[] = [];
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of Array.from(listeners)) listener();
}

/** Called by an open modal dialog with its slot. The returned function takes it off again. */
export function registerModalSlot(slot: HTMLElement): () => void {
  slots.push(slot);
  notify();
  return () => {
    const index = slots.lastIndexOf(slot);
    if (index === -1) return;
    slots.splice(index, 1);
    notify();
  };
}

/** The slot of the innermost open modal dialog, or `null` when none is open. */
export function topModalSlot(): HTMLElement | null {
  return slots[slots.length - 1] ?? null;
}

/** Runs whenever a modal opens or closes. The returned function stops listening. */
export function subscribeModalSlots(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
